import { z } from "zod";

/**
 * Queue names and job payloads shared by API (producer) and worker (consumer).
 * `orgId` in the CPU jobs orders the queue fairly between organizations
 * (apps/backend/src/queue/queues.ts); jobs from before it existed lack it.
 */
export const QUEUES = {
  /** Short jobs: thumbnails, metadata. */
  media: "media",
  /** Periodic housekeeping: trash purge, stale uploads, expired exports. */
  maintenance: "maintenance",
  /** Light rendering: document thumbnails, PNG/JPG and digital PDF exports. */
  render: "render",
  /** Heavy jobs, strictly one after another: print PDF, video export, transcription. */
  heavy: "heavy",
  /** Background removal of images, one at a time (someone in the editor waits for it). */
  cutout: "cutout",
  /** Notification mails, bundled per recipient (delayed jobs). */
  notify: "notify",
  /** Cuts the person out of videos (minutes per video), one at a time. */
  matte: "matte",
} as const;

export const mediaProcessJob = z.object({ assetId: z.uuid() });
export type MediaProcessJob = z.infer<typeof mediaProcessJob>;

export const MEDIA_JOBS = {
  /** Metadata and thumbnail right after the upload. */
  process: "process",
  /** Videos/audio: editor preview, filmstrip, waveform, audio for the transcription. */
  prepare: "prepare",
} as const;

/** Local speech recognition (whisper.cpp), in the heavy queue. */
export const transcribeJob = z.object({ assetId: z.uuid(), orgId: z.string().optional() });
export type TranscribeJob = z.infer<typeof transcribeJob>;

export const HEAVY_JOBS = {
  export: "export",
  transcribe: "transcribe",
} as const;

/** Removes the background of an image: `assetId` is the new, cut-out copy. */
export const cutoutJob = z.object({ assetId: z.uuid(), orgId: z.string().optional() });
export type CutoutJob = z.infer<typeof cutoutJob>;

export const CUTOUT_JOBS = {
  run: "cutout",
} as const;

/** Cuts the person out of every frame of a video (`assetId` is the video itself). */
export const matteJob = z.object({ assetId: z.uuid(), orgId: z.string().optional() });
export type MatteJob = z.infer<typeof matteJob>;

export const MATTE_JOBS = {
  run: "matte",
} as const;

export const exportJob = z.object({ exportId: z.uuid(), orgId: z.string().optional() });
export type ExportJob = z.infer<typeof exportJob>;

export const thumbnailJob = z.object({
  documentId: z.uuid(),
  revision: z.number().int(),
  /** Re-render although the revision already has a thumbnail (brand kit changed). */
  force: z.boolean().optional(),
});
export type ThumbnailJob = z.infer<typeof thumbnailJob>;

/** Renders a device mockup into a new image of the media library: `assetId` is that image. */
export const mockupJob = z.object({ assetId: z.uuid(), orgId: z.string().optional() });
export type MockupJob = z.infer<typeof mockupJob>;

export const RENDER_JOBS = {
  thumbnail: "thumbnail",
  export: "export",
  mockup: "mockup",
} as const;

export const MAINTENANCE_JOBS = {
  purgeTrash: "purge-trash",
  cleanupUploads: "cleanup-uploads",
  purgeExports: "purge-exports",
  /** Deletes notifications older than NOTIFICATION_RETENTION_DAYS. */
  purgeNotifications: "purge-notifications",
  /** Schedules notification mails whose job got lost (e.g. Redis restarted). */
  notificationMails: "notification-mails",
} as const;

/** Sends one bundled mail with a user's pending notifications. */
export const notificationDigestJob = z.object({
  userId: z.string(),
  /** Planned run time (ms), kept up to date when a mention moves the job forward. */
  runAt: z.number().optional(),
});
export type NotificationDigestJob = z.infer<typeof notificationDigestJob>;

export const NOTIFY_JOBS = {
  digest: "digest",
} as const;

/** Days an item stays in the trash before it is deleted for good. */
export const TRASH_RETENTION_DAYS = 30;
